import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import { ChevronRight as ChevronIcon } from '@mui/icons-material';
import { tokens } from '../../../../theme/tokens';
import { StagedSourceBadge } from '../../../StagedSourceBadge';
import { BannerAction, DetailCard, Field, FieldGrid, LinkButton, RemoveX, TabBody, TextInput, WarningBanner } from '../primitives';
import { DimsDraft, ProductDraft, SpecGroupDraft, specificationsFrom } from '../productDraft';
import type { AdminCanonicalProduct, PhysicalFromSpecs, SpecDuplicateIssue } from '../productDetailTypes';
import { fieldSource } from '../productDetailFormat';

interface Props {
  product: AdminCanonicalProduct;
  draft: ProductDraft;
  base: ProductDraft;
  editing: boolean;
  update: (patch: Partial<ProductDraft>) => void;
  duplicateSpecs: SpecDuplicateIssue | null;
  physicalFromSpecs?: PhysicalFromSpecs | null;
  /** Saves a one-click fix straight away (not part of the edit draft). */
  applyFix: (payload: Record<string, unknown>, message: string) => Promise<void>;
  fixing: boolean;
}

const humanize = (key: string) =>
  key
    .split('.')
    .map((part) => part.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/[_-]+/g, ' '))
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' › ');

const groupLabel = (name: string) => (name ? humanize(name) : 'General');

export const SpecsTab: React.FC<Props> = ({ product, draft, base, editing, update, duplicateSpecs, physicalFromSpecs, applyFix, fixing }) => {
  const [closed, setClosed] = useState<Record<string, boolean>>({});
  const groups = draft.specGroups;
  const valueCount = groups.reduce((sum, g) => sum + g.rows.length, 0);
  const anyClosed = Object.values(closed).some(Boolean);
  const specsBadge = fieldSource(product, draft.specGroups, base.specGroups, 'specifications', 'specs');
  const setGroup = (index: number, next: SpecGroupDraft) => update({ specGroups: groups.map((g, i) => (i === index ? next : g)) });

  const addGroup = () => {
    const taken = new Set(groups.map((g) => g.name));
    let n = groups.filter((g) => g.name).length + 1;
    while (taken.has(`New group ${n}`)) n += 1;
    update({ specGroups: [...groups, { name: `New group ${n}`, rows: [{ key: '', value: '' }], isGroup: true }] });
  };

  const dropFlat = () => {
    const kept = groups.filter((g) => g.isGroup);
    return applyFix({ specifications: specificationsFrom(kept, product.specifications) }, 'Flat copy removed — grouped specs kept');
  };

  return (
    <TabBody>
      {duplicateSpecs && (
        <Box sx={{ border: `1px solid ${tokens.signal.caution.border}`, bgcolor: tokens.signal.caution.tint, borderRadius: `${tokens.radius.card}px`, overflow: 'hidden' }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, px: 2, py: 1.5, flexWrap: 'wrap' }}>
            <Box sx={{ width: 6, height: 6, mt: '6px', borderRadius: '50%', bgcolor: tokens.signal.caution.solid, flex: '0 0 auto' }} />
            <Box sx={{ flex: '1 1 300px', display: 'flex', flexDirection: 'column', gap: 0.25 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: tokens.signal.caution.ink }}>Specs are stored twice</Typography>
              <Typography sx={{ fontSize: 12.5, lineHeight: 1.5, color: tokens.signal.caution.ink }}>
                {duplicateSpecs.flatCount} flat keys and {duplicateSpecs.groupCount} grouped sections hold the same specs.{' '}
                {duplicateSpecs.diffs.length === 1 ? '1 value differs.' : `${duplicateSpecs.diffs.length} values differ.`} The grouped copy is the one to keep.
              </Typography>
            </Box>
            <BannerAction onClick={dropFlat} disabled={fixing || editing} title={editing ? 'Save or discard your edits first' : undefined}>
              Keep grouped, drop flat copy
            </BannerAction>
          </Box>
          {duplicateSpecs.diffs.length > 0 && (
            <Box sx={{ borderTop: `1px solid ${tokens.signal.caution.border}`, bgcolor: tokens.signal.caution.row, overflowX: 'auto' }}>
              <Box sx={{ minWidth: 480 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(90px, 140px) minmax(0, 1fr) minmax(0, 1fr)', md: 'minmax(90px, 140px) minmax(0, 1fr) minmax(0, 1fr)' }, gap: 1.5, px: 2, pt: 1, pb: 0.5, fontSize: 10.5, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: tokens.signal.caution.ink }}>
                <span>Spec</span>
                <span>Flat copy</span>
                <span>Grouped copy (kept)</span>
              </Box>
              {duplicateSpecs.diffs.map((d) => (
                <Box key={d.key} sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(90px, 140px) minmax(0, 1fr) minmax(0, 1fr)', md: 'minmax(90px, 140px) minmax(0, 1fr) minmax(0, 1fr)' }, gap: 1.5, px: 2, py: '7px', borderTop: `1px solid ${tokens.signal.caution.border}`, fontSize: 12.5 }}>
                  <Typography component="span" sx={{ fontSize: 12.5, color: tokens.ink.muted }}>{d.key}</Typography>
                  <Typography component="span" sx={{ fontFamily: tokens.font.mono, fontSize: 12, color: tokens.ink.faint, textDecoration: 'line-through', overflowWrap: 'anywhere' }}>{d.flat}</Typography>
                  <Typography component="span" sx={{ fontSize: 12.5, color: tokens.ink.primary, overflowWrap: 'anywhere' }}>{d.grouped}</Typography>
                </Box>
              ))}
              </Box>
            </Box>
          )}
        </Box>
      )}

      <DetailCard
        title="Specifications"
        count={`${valueCount} values · ${groups.filter((g) => g.name).length} groups`}
        note={
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
            {duplicateSpecs ? 'Grouped copy' : 'Raw copy kept in Source & history'}
            <StagedSourceBadge source={specsBadge} />
          </Box>
        }
        actions={
          <>
            {editing && <LinkButton onClick={addGroup}>+ Add group</LinkButton>}
            {groups.length > 0 && (
              <LinkButton onClick={() => setClosed(anyClosed ? {} : Object.fromEntries(groups.map((g) => [g.name, true])))}>
                {anyClosed ? 'Expand all' : 'Collapse all'}
              </LinkButton>
            )}
          </>
        }
      >
        {groups.length === 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: '18px', pb: 2 }}>
            <Typography sx={{ fontSize: 13, color: tokens.ink.faint }}>No specifications yet.</Typography>
            {editing && <LinkButton onClick={() => update({ specGroups: [{ name: '', rows: [{ key: '', value: '' }] }] })}>+ Add spec</LinkButton>}
          </Box>
        )}
        {groups.map((group, gi) => {
          const open = !closed[group.name];
          return (
            <Box key={gi} sx={{ borderTop: `1px solid ${tokens.line.hairline}` }}>
              <Box
                component="button"
                type="button"
                aria-expanded={open}
                onClick={() => setClosed((c) => ({ ...c, [group.name]: open }))}
                sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', px: '18px', py: 1.25, border: 'none', bgcolor: tokens.surface.subtle, textAlign: 'left', cursor: 'pointer', font: 'inherit', '&:hover': { bgcolor: tokens.surface.sunken } }}
              >
                <ChevronIcon sx={{ fontSize: 16, color: tokens.ink.muted, transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }} />
                <Typography component="span" sx={{ fontSize: 12.5, fontWeight: 600, color: tokens.ink.primary }}>{groupLabel(group.name)}</Typography>
                <Typography component="span" sx={{ fontSize: 12, color: tokens.ink.faint }}>{group.rows.length}</Typography>
              </Box>
              {editing && group.isGroup && open && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: '18px', pl: '36px', py: 1, borderTop: `1px solid ${tokens.line.faint}` }}>
                  <Typography component="span" sx={{ fontSize: 12, color: tokens.ink.muted, flex: '0 0 auto' }}>Group name</Typography>
                  <TextInput
                    ariaLabel={`Name of group ${gi}`}
                    value={group.name}
                    onChange={(name) => setGroup(gi, { ...group, name })}
                    sx={{ maxWidth: 320 }}
                  />
                </Box>
              )}
              {open &&
                group.rows.map((row, ri) => (
                  <Box
                    key={ri}
                    sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0,1fr)', md: editing ? 'minmax(110px, 200px) minmax(0, 1fr) auto' : 'minmax(110px, 200px) minmax(0, 1fr)' }, gap: 2, alignItems: 'center', minHeight: 40, px: '18px', pl: '36px', py: '7px', borderTop: `1px solid ${tokens.line.faint}` }}
                  >
                    {editing ? (
                      <TextInput
                        ariaLabel={`Spec name ${row.key}`}
                        value={row.key}
                        onChange={(key) => setGroup(gi, { ...group, rows: group.rows.map((r, j) => (j === ri ? { ...r, key } : r)) })}
                        sx={{ '& .MuiInputBase-root': { fontSize: 12.5 } }}
                      />
                    ) : (
                      <Typography component="span" sx={{ fontSize: 12.5, color: tokens.ink.muted }}>{humanize(row.key)}</Typography>
                    )}
                    {editing ? (
                      <TextInput
                        ariaLabel={`${row.key} value`}
                        value={row.value}
                        onChange={(value) => setGroup(gi, { ...group, rows: group.rows.map((r, j) => (j === ri ? { ...r, value } : r)) })}
                      />
                    ) : (
                      <Typography component="span" sx={{ fontSize: 13, lineHeight: 1.5, color: tokens.ink.primary, overflowWrap: 'anywhere', fontVariantNumeric: 'tabular-nums' }}>
                        {row.value || '—'}
                      </Typography>
                    )}
                    {editing && <RemoveX label={`Remove ${row.key}`} onClick={() => setGroup(gi, { ...group, rows: group.rows.filter((_, j) => j !== ri) })} />}
                  </Box>
                ))}
              {open && editing && (
                <Box sx={{ px: '18px', pl: '36px', py: 1, borderTop: `1px solid ${tokens.line.faint}` }}>
                  <LinkButton onClick={() => setGroup(gi, { ...group, rows: [...group.rows, { key: '', value: '' }] })}>+ Add spec</LinkButton>
                </Box>
              )}
            </Box>
          );
        })}
      </DetailCard>

      {physicalFromSpecs && (
        <WarningBanner
          title="Physical size is out of step with the spec text"
          action={
            <BannerAction
              onClick={() => applyFix(
                { dimensions: physicalFromSpecs.dimensions },
                'Physical size updated from the spec text',
              )}
              disabled={fixing || editing}
              title={editing ? 'Save or discard your edits first' : undefined}
            >
              Use the spec text
            </BannerAction>
          }
        >
          The spec text reads as {physicalFromSpecs.summary}. Use that for the physical fields.
        </WarningBanner>
      )}

      <DimsCard
        title="Physical"
        note="dimensions"
        product={product}
        path="dimensions"
        draft={draft.dimensions}
        base={base.dimensions}
        units={{ size: product.dimensions?.unit || 'mm', weight: product.dimensions?.weightUnit || 'g' }}
        editing={editing}
        onChange={(dimensions) => update({ dimensions })}
      />
      <DimsCard
        title="Shipping"
        note="shippingDimensions · packaged"
        product={product}
        path="shippingDimensions"
        draft={draft.shippingDimensions}
        base={base.shippingDimensions}
        units={{ size: product.shippingDimensions?.unit || 'mm', weight: product.shippingDimensions?.weightUnit || 'g' }}
        editing={editing}
        onChange={(shippingDimensions) => update({ shippingDimensions })}
        hint={product.shippingDimensions?.source ? `source: ${product.shippingDimensions.source}` : undefined}
      />
    </TabBody>
  );
};

const DimsCard: React.FC<{
  title: string;
  note: string;
  product: AdminCanonicalProduct;
  path: 'dimensions' | 'shippingDimensions';
  draft: DimsDraft;
  base: DimsDraft;
  units: { size: string; weight: string };
  editing: boolean;
  onChange: (next: DimsDraft) => void;
  hint?: string;
}> = ({ title, note, product, path, draft, base, units, editing, onChange, hint }) => {
  const fields: Array<{ key: keyof DimsDraft; label: string; unit: string }> = [
    { key: 'length', label: 'Length', unit: units.size },
    { key: 'width', label: 'Width', unit: units.size },
    { key: 'height', label: 'Height', unit: units.size },
    { key: 'weight', label: 'Weight', unit: units.weight },
  ];
  return (
    <DetailCard title={title} note={note}>
      <FieldGrid min={160}>
        {fields.map((f) => (
          <Field
            key={f.key}
            label={`${f.label} (${f.unit})`}
            source={fieldSource(product, draft[f.key], base[f.key], path)}
            display={draft[f.key] ? `${draft[f.key]} ${f.unit}` : ''}
            hint={f.key === 'weight' ? hint : undefined}
            editor={editing ? <TextInput ariaLabel={`${title} ${f.label}`} value={draft[f.key]} placeholder="0" onChange={(value) => onChange({ ...draft, [f.key]: value })} /> : undefined}
          />
        ))}
      </FieldGrid>
    </DetailCard>
  );
};
